import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { ensureConversation, fetchMessages, sendMessage, fetchConversationSummaries } from '../../lib/chatService.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { Avatar } from '../components/MarketplaceAtoms.js';
import { moderateChatMessage } from '../../lib/moderationService.js';

function normalizeThread(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    return {
      sellerUsername: raw.replace(/^@/, ''),
      sellerUserId: null,
      listingId: null,
      listingTitle: null,
      listingPrice: null,
      listingCoverUrl: null,
      listingArtist: null,
      listingCondition: null,
      listingStatus: null,
      sellerAvatarUrl: null,
      isAuction: false,
      conversationId: null,
    };
  }
  return {
    sellerUsername: String(raw.sellerUsername || raw.seller || '')
      .replace(/^@/, '')
      .trim(),
    sellerUserId: raw.sellerUserId || null,
    listingId: raw.listingId || null,
    listingTitle: raw.listingTitle ?? null,
    listingPrice: raw.listingPrice != null ? raw.listingPrice : null,
    listingCoverUrl: raw.listingCoverUrl ?? null,
    listingArtist: raw.listingArtist ?? null,
    listingCondition: raw.listingCondition ?? null,
    listingStatus: raw.listingStatus ?? null,
    sellerAvatarUrl: raw.sellerAvatarUrl ?? null,
    isAuction: !!raw.isAuction,
    conversationId: raw.conversationId || null,
  };
}

function formatMsgTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatListTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function safeChatText(msg) {
  if (!msg) return '';
  const st = String(msg.moderation_status || 'approved');
  if (st === 'hidden') return 'Mensaje ocultado por moderación';
  return String(msg.content || '');
}

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

/**
 * @param {{ onBack: () => void, onNavigate: (p: string, o?: object) => void, user: object|null, chatContext: object|string|null }} props
 */
export default function PageChat({ onBack, onNavigate, user, chatContext }) {
  const initial = useMemo(() => normalizeThread(chatContext), [chatContext]);
  const openedAsDeepLink = useRef(!!initial?.sellerUserId || !!initial?.conversationId);

  const [view, setView] = useState(initial?.sellerUserId || initial?.conversationId ? 'chat' : 'list');
  const [activeMeta, setActiveMeta] = useState(initial);
  const [conversationId, setConversationId] = useState(initial?.conversationId || null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [deletingConvId, setDeletingConvId] = useState(null);
  const [listTab, setListTab] = useState('recibidos'); // recibidos | enviados
  const listEndRef = useRef(null);

  // ── REPORTAR MENSAJE ──────────────
  const [reportOpen, setReportOpen] = useState(false); // modal
  const [reportTarget, setReportTarget] = useState(null); // { id, sender_id, created_at, content }
  const [reportCategory, setReportCategory] = useState('abuse');
  const [reportBody, setReportBody] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportFlash, setReportFlash] = useState('');

  const scrollToBottom = useCallback(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, []);

  const openReport = useCallback((m) => {
    if (!user?.id || !conversationId || !m?.id) return;
    setReportFlash('');
    setReportCategory('abuse');
    setReportBody('');
    setReportTarget({ id: m.id, sender_id: m.sender_id, created_at: m.created_at, content: m.content });
    setReportOpen(true);
  }, [user?.id, conversationId]);

  const submitReport = useCallback(async () => {
    if (!user?.id || !conversationId || !reportTarget?.id) return;
    if (reportBusy) return;
    if (String(reportBody || '').trim().length < 6) {
      setReportFlash('Cuéntanos un poco más (mínimo 6 caracteres).');
      return;
    }
    setReportBusy(true);
    setReportFlash('');
    try {
      const { error } = await supabase.from('message_reports').insert({
        message_id: reportTarget.id,
        conversation_id: conversationId,
        reporter_id: user.id,
        category: reportCategory,
        body: String(reportBody || '').trim(),
      });
      if (error) throw error;
      setReportFlash('Reporte recibido correctamente. También te lo enviamos a Notificaciones.');
      setTimeout(() => setReportOpen(false), 700);
    } catch (e) {
      setReportFlash('No se pudo enviar el reporte. Inténtalo de nuevo.');
    } finally {
      setReportBusy(false);
    }
  }, [user?.id, conversationId, reportTarget, reportBusy, reportBody, reportCategory]);

  const loadThread = useCallback(
    async meta => {
      if (!user?.id || !meta) return;
      setThreadError(null);
      setLoadingThread(true);
      try {
        let cid = meta.conversationId;
        if (!cid && meta.sellerUserId) {
          const { conversationId: newId, error } = await ensureConversation(supabase, {
            sellerId: meta.sellerUserId,
            listingId: meta.listingId,
          });
          if (error || !newId) {
            setThreadError(error || 'No se pudo abrir la conversación.');
            setLoadingThread(false);
            return;
          }
          cid = newId;
          setConversationId(newId);
        }
        if (!cid) {
          setThreadError('Falta el identificador del vendedor.');
          setLoadingThread(false);
          return;
        }
        setConversationId(cid);
        const { messages: rows, error: mErr } = await fetchMessages(supabase, cid);
        if (mErr) setThreadError(mErr);
        setMessages(rows);
      } finally {
        setLoadingThread(false);
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (view !== 'chat' || !activeMeta) return;
    loadThread(activeMeta);
  }, [view, activeMeta, loadThread]);

  useEffect(() => {
    if (view !== 'chat' || !activeMeta?.listingId) return;
    const needsHydrate =
      !activeMeta?.listingTitle ||
      activeMeta?.listingPrice == null ||
      !activeMeta?.listingCoverUrl ||
      !activeMeta?.listingArtist;
    if (!needsHydrate) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('listings')
        .select('title, artist, price, is_auction, cover_url, condition, status')
        .eq('id', activeMeta.listingId)
        .maybeSingle();
      if (cancelled || !data) return;
      setActiveMeta(prev => {
        if (!prev || prev.listingId !== activeMeta.listingId) return prev;
        return {
          ...prev,
          listingTitle: data.title ?? prev.listingTitle,
          listingArtist: data.artist ?? prev.listingArtist,
          listingPrice: data.price ?? prev.listingPrice,
          isAuction: typeof data.is_auction === 'boolean' ? data.is_auction : prev.isAuction,
          listingCoverUrl: data.cover_url ?? prev.listingCoverUrl,
          listingCondition: data.condition ?? prev.listingCondition,
          listingStatus: data.status ?? prev.listingStatus,
        };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [view, activeMeta]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (view !== 'chat' || !conversationId || !user?.id) return;
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        payload => {
          const row = payload.new;
          if (!row?.id) return;
          setMessages(prev => {
            if (prev.some(m => m.id === row.id)) return prev;
            return [
              ...prev,
              {
                id: row.id,
                sender_id: row.sender_id,
                content: row.content,
                created_at: row.created_at,
                moderation_status: row.moderation_status,
              },
            ];
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [view, conversationId, user?.id]);

  useEffect(() => {
    if (view !== 'list' || !user?.id) return;
    let cancelled = false;
    setLoadingList(true);
    fetchConversationSummaries(supabase, user.id)
      .then(rows => {
        if (!cancelled) setSummaries(rows);
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, user?.id]);

  const handleSend = useCallback(async () => {
    const text = msg.trim();
    if (!text || !user?.id || !conversationId || sending) return;
    const mm = moderateChatMessage(text);
    if (!mm.approved) {
      setThreadError(`No se pudo enviar · ${mm.reason || 'Revisa el texto.'}`);
      return;
    }
    setSending(true);
    setThreadError(null);
    const { error } = await sendMessage(supabase, { conversationId, senderId: user.id, content: text });
    setSending(false);
    if (error) {
      setThreadError(error === 'empty' ? '' : error);
      return;
    }
    setMsg('');
  }, [msg, user?.id, conversationId, sending]);

  const openSummaryRow = useCallback(async row => {
    let listingTitle = null;
    let listingPrice = null;
    let isAuction = false;
    let listingCoverUrl = null;
    let listingArtist = null;
    let listingCondition = null;
    let listingStatus = null;
    const listingId = row.listing_id;
    if (listingId) {
      const { data: listing } = await supabase
        .from('listings')
        .select('title, artist, price, is_auction, cover_url, condition, status')
        .eq('id', listingId)
        .maybeSingle();
      if (listing) {
        listingTitle = listing.title;
        listingPrice = listing.price;
        isAuction = !!listing.is_auction;
        listingCoverUrl = listing.cover_url ?? null;
        listingArtist = listing.artist ?? null;
        listingCondition = listing.condition ?? null;
        listingStatus = listing.status ?? null;
      }
    }
    setActiveMeta({
      sellerUsername: row.otherUsername,
      sellerUserId: null,
      listingId,
      listingTitle,
      listingPrice,
      listingCoverUrl,
      listingArtist,
      listingCondition,
      listingStatus,
      sellerAvatarUrl: row.otherAvatarUrl ?? null,
      isAuction,
      conversationId: row.id,
    });
    setConversationId(row.id);
    setMessages([]);
    setView('chat');
  }, []);

  const deleteConversation = useCallback(
    async row => {
      if (!user?.id || !row?.id) return;
      if (!window.confirm('¿Eliminar esta conversación de tu bandeja?')) return;
      setDeletingConvId(row.id);
      const patch = {};
      if (row.buyer_id === user.id) patch.buyer_deleted_at = new Date().toISOString();
      else if (row.seller_id === user.id) patch.seller_deleted_at = new Date().toISOString();
      const { error } = await supabase.from('conversations').update(patch).eq('id', row.id);
      setDeletingConvId(null);
      if (error) return;
      setSummaries(prev => prev.filter(x => x.id !== row.id));
    },
    [user?.id]
  );

  const handleChatHeaderBack = () => {
    if (openedAsDeepLink.current) {
      onBack();
      return;
    }
    setView('list');
    setActiveMeta(null);
    setConversationId(null);
    setMessages([]);
  };

  const openListingFromStrip = useCallback(async () => {
    if (!activeMeta?.listingId) return;
    const { data } = await supabase
      .from('listings')
      .select(
        `
        *,
        profiles ( username, avatar_url, is_verified, is_store, partner_active_roles )
      `
      )
      .eq('id', activeMeta.listingId)
      .maybeSingle();
    if (!data) return;
    const { mapListing } = await import('../../lib/listingsService.js');
    onNavigate('ficha', mapListing(data));
  }, [activeMeta?.listingId, onNavigate]);

  if (!user?.id) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Mensajes" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 520, margin: '40px auto', padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
          Inicia sesión para ver tus mensajes y escribir al vendedor.
        </div>
      </div>
    );
  }

  if (view === 'list') {
    const filteredSummaries =
      listTab === 'enviados'
        ? summaries.filter(r => r.role === 'sent')
        : summaries.filter(r => r.role === 'received');
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar
          onBack={onBack}
          title="Mensajes"
          mini
          dark
          user={user}
          onNavigate={onNavigate}
          onSignOut={onBack}
        />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 20px 60px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, fontWeight: 500, color: '#fff' }}>Mensajes</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                ['recibidos', 'Recibidos'],
                ['enviados', 'Enviados'],
              ].map(([k, label]) => {
                const act = listTab === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setListTab(k)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      fontSize: 11,
                      cursor: 'pointer',
                      border: `0.5px solid ${act ? C.gold : 'rgba(255,255,255,0.15)'}`,
                      color: act ? C.gold : 'rgba(255,255,255,0.6)',
                      background: act ? 'rgba(200,168,75,0.12)' : 'transparent',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {loadingList ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', padding: '24px 0' }}>Cargando…</div>
          ) : filteredSummaries.length === 0 ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', padding: '24px 0', lineHeight: 1.5 }}>
              {listTab === 'enviados'
                ? 'Aún no has enviado mensajes. Abre un anuncio y pulsa «Enviar mensaje al vendedor».'
                : 'Aún no has recibido mensajes.'}
            </div>
          ) : (
            filteredSummaries.map(row => (
              <div
                key={row.id}
                onClick={() => openSummaryRow(row)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: '0.5px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                }}
              >
                <Avatar
                  initials={(row.otherUsername || '?').slice(0, 2).toUpperCase()}
                  bg="#534AB7"
                  color="#EEEDFE"
                  size={44}
                  imageUrl={row.otherAvatarUrl}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>@{row.otherUsername}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{formatListTime(row.updated_at)}</span>
                  </div>
                  {row.preview ? (
                    <span
                      style={{
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.4)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      }}
                    >
                      {row.preview}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.22)' }}>Sin mensajes aún</span>
                  )}
                </div>
                <button
                  type="button"
                  title="Eliminar conversación"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteConversation(row);
                  }}
                  disabled={deletingConvId === row.id}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: deletingConvId === row.id ? 'wait' : 'pointer',
                    padding: 6,
                    color: 'rgba(255,255,255,0.35)',
                    opacity: deletingConvId === row.id ? 0.55 : 1,
                  }}
                >
                  <Icon name="trash" size={15} color="rgba(255,255,255,0.35)" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  const title = activeMeta?.sellerUsername ? `@${activeMeta.sellerUsername}` : 'Chat';
  const showListingStrip = !!activeMeta?.listingTitle;

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: C.dark,
          borderBottom: '0.5px solid rgba(255,255,255,0.08)',
          padding: '50px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <button type="button" onClick={handleChatHeaderBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <Icon name="back" size={18} color="rgba(255,255,255,0.6)" />
        </button>
        <Avatar
          initials={(activeMeta?.sellerUsername || '?').slice(0, 2).toUpperCase()}
          bg="#534AB7"
          color="#EEEDFE"
          size={36}
          imageUrl={activeMeta?.sellerAvatarUrl}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>{title}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Mensajes directos</div>
        </div>
        <button
          type="button"
          onClick={() => onNavigate('pedido')}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="bag" size={14} color="rgba(255,255,255,0.6)" />
        </button>
      </div>

      {showListingStrip ? (
        <button
          type="button"
          onClick={openListingFromStrip}
          disabled={!activeMeta?.listingId}
          style={{
            margin: '8px 16px',
            background: C.darkMid,
            borderRadius: 10,
            padding: '10px',
            border: '0.5px solid rgba(255,255,255,0.08)',
            cursor: activeMeta?.listingId ? 'pointer' : 'default',
            textAlign: 'left',
            width: 'calc(100% - 32px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                background: 'rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {activeMeta.listingCoverUrl ? (
                <img src={activeMeta.listingCoverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Icon name="disc" size={14} color="rgba(255,255,255,0.4)" />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#fff',
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {activeMeta.listingTitle}
              </div>
              <div
                style={{
                  marginTop: 3,
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.45)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {[activeMeta.listingArtist, activeMeta.listingCondition].filter(Boolean).join(' · ') || 'Ficha del anuncio'}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.gold }}>
                {activeMeta.listingPrice != null && activeMeta.listingPrice !== '' ? formatEuro(activeMeta.listingPrice) : '—'}
              </div>
              <div style={{ marginTop: 3, fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                {activeMeta.isAuction ? 'Subasta' : 'Venta'}
                {activeMeta.listingStatus ? ` · ${String(activeMeta.listingStatus).replace(/_/g, ' ')}` : ''}
              </div>
            </div>
          </div>
        </button>
      ) : null}

      {threadError ? (
        <div style={{ margin: '8px 16px', fontSize: 12, color: '#f0a8a8', lineHeight: 1.4 }}>{threadError}</div>
      ) : null}

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loadingThread ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: 12 }}>Cargando mensajes…</div>
        ) : messages.length === 0 ? null : (
          messages.map(m => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} style={mine ? { alignSelf: 'flex-end' } : undefined}>
                <div className={mine ? 'msg-out' : 'msg-in'} style={String(m.moderation_status || 'approved') === 'hidden' ? { fontStyle: 'italic', opacity: 0.8 } : undefined}>
                  {safeChatText(m)}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: mine ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.25)',
                    marginTop: 2,
                    textAlign: mine ? 'right' : 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    justifyContent: mine ? 'flex-end' : 'flex-start',
                  }}
                >
                  <span>{formatMsgTime(m.created_at)}</span>
                  {!mine ? (
                    <button
                      type="button"
                      onClick={() => openReport(m)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: 'rgba(255,255,255,0.35)',
                        cursor: 'pointer',
                        fontSize: 9,
                        textDecoration: 'underline',
                      }}
                    >
                      Reportar
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        <div ref={listEndRef} />
      </div>

      {reportOpen ? (
        <div
          onClick={() => { if (!reportBusy) setReportOpen(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.62)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 60,
            padding: 14,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(520px, 96vw)',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.12)',
              padding: 16,
              boxShadow: '0 20px 80px rgba(0,0,0,0.35)',
              color: '#111',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: '0.98rem' }}>Reportar mensaje</div>
                <div style={{ fontSize: '0.8rem', color: '#777', marginTop: 2 }}>
                  {reportTarget?.created_at ? `Enviado a las ${formatMsgTime(reportTarget.created_at)}` : ''}
                </div>
              </div>
              <button
                type="button"
                disabled={reportBusy}
                onClick={() => setReportOpen(false)}
                style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '6px 10px', borderRadius: 8, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Motivo</div>
                <select
                  value={reportCategory}
                  onChange={e => setReportCategory(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', fontWeight: 700 }}
                >
                  <option value="abuse">Abuso / insultos</option>
                  <option value="spam">Spam</option>
                  <option value="scam">Posible estafa</option>
                  <option value="harassment">Acoso</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Detalles</div>
                <textarea
                  value={reportBody}
                  onChange={e => setReportBody(e.target.value)}
                  placeholder="Describe el problema para que podamos revisarlo rápido."
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {reportFlash ? (
                <div style={{ fontSize: '0.78rem', color: reportFlash.includes('Gracias') ? C.green : C.warn, fontWeight: 800 }}>
                  {reportFlash}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 2 }}>
                <button
                  type="button"
                  disabled={reportBusy}
                  onClick={() => setReportOpen(false)}
                  style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={reportBusy}
                  onClick={() => void submitReport()}
                  style={{ background: C.gold, color: '#000', border: 'none', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', fontWeight: 900 }}
                >
                  {reportBusy ? 'Enviando…' : 'Enviar reporte'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        style={{
          padding: '10px 14px',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          borderTop: '0.5px solid rgba(255,255,255,0.08)',
          background: C.darkNav,
        }}
      >
        <input
          value={msg}
          onChange={e => setMsg(e.target.value)}
          placeholder="Escribe un mensaje…"
          disabled={!conversationId || !!threadError}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          style={{
            flex: 1,
            padding: '9px 14px',
            borderRadius: 20,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.07)',
            color: '#fff',
            fontSize: 13,
            outline: 'none',
            fontFamily: 'inherit',
          }}
          onFocus={e => {
            e.target.style.borderColor = C.gold;
          }}
          onBlur={e => {
            e.target.style.borderColor = 'rgba(255,255,255,0.12)';
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !msg.trim() || !conversationId || !!threadError}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: C.gold,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: sending ? 'wait' : 'pointer',
            opacity: sending ? 0.7 : 1,
          }}
        >
          <Icon name="send" size={15} color="#0a0a0f" />
        </button>
      </div>
    </div>
  );
}
